import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Command as CommandIcon, ArrowRight, Mic, Bot, Sparkles, Plus, Compass, Calendar, Lightbulb, Puzzle, Plug, Brain, Home, Settings as SettingsIcon, Clapperboard, Activity, CheckCircle2, ShieldAlert } from 'lucide-react';

const COMMANDS = [
  { id: 'go-dashboard', label: 'Go to Dashboard', icon: Home, path: '/dashboard', section: 'Navigate' },
  { id: 'go-coach', label: 'Talk to Coach', icon: Compass, path: '/coach', section: 'Navigate' },
  { id: 'go-myday', label: 'Go to MyDay', icon: Calendar, path: '/myday', section: 'Navigate' },
  { id: 'go-ideas', label: 'Open Ideas Bank', icon: Lightbulb, path: '/ideas', section: 'Navigate' },
  { id: 'go-ugc', label: 'Open UGC Studio', icon: Clapperboard, path: '/ugc', section: 'Navigate' },
  { id: 'go-modules', label: 'View Modules', icon: Puzzle, path: '/modules', section: 'Navigate' },
  { id: 'go-connections', label: 'Manage Connections', icon: Plug, path: '/connections', section: 'Navigate' },
  { id: 'go-ai-brain', label: 'Configure AI Brain', icon: Brain, path: '/ai-brain', section: 'Navigate' },
  { id: 'go-settings', label: 'Open Settings', icon: SettingsIcon, path: '/settings', section: 'Navigate' },
  { id: 'new-reel', label: 'Start a New Reel', icon: Plus, path: '/dashboard?new=1', section: 'Create' },
  { id: 'new-workspace', label: 'Create a Workspace', icon: Plus, path: '/workspaces', section: 'Create' },
  { id: 'filter-in-progress', label: 'Filter: In Progress reels', icon: Activity, path: '/dashboard?filter=in_progress', section: 'Filter' },
  { id: 'filter-approved', label: 'Filter: Approved reels', icon: CheckCircle2, path: '/dashboard?filter=approved', section: 'Filter' },
  { id: 'filter-anti-ai-failed', label: 'Filter: Anti-AI failed reels', icon: ShieldAlert, path: '/dashboard?filter=anti_ai_failed', section: 'Filter' },
];

/**
 * Command Palette, Cmd+K / Ctrl+K to open.
 * Global keyboard-driven navigation and actions.
 */
export const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery('');
        setActiveIdx(0);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Close on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const filtered = COMMANDS.filter((c) =>
    c.label.toLowerCase().includes(query.toLowerCase()) ||
    c.section.toLowerCase().includes(query.toLowerCase())
  );

  const grouped = filtered.reduce((acc, c) => {
    if (!acc[c.section]) acc[c.section] = [];
    acc[c.section].push(c);
    return acc;
  }, {});

  const flat = Object.values(grouped).flat();

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter' && flat[activeIdx]) {
      e.preventDefault();
      navigate(flat[activeIdx].path);
      setOpen(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4" onClick={() => setOpen(false)}>
      <div
        className="w-full max-w-xl bg-card border border-border rounded-2xl shadow-2xl overflow-hidden fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Search className="w-5 h-5 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKey}
            placeholder="Type a command or search..."
            className="flex-1 bg-transparent outline-none text-base"
            data-testid="command-palette-input"
          />
          <kbd className="text-xs px-2 py-0.5 rounded bg-secondary text-muted-foreground font-mono">ESC</kbd>
        </div>

        <div className="max-h-[400px] overflow-y-auto p-2">
          {flat.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">No results for "{query}"</p>
          ) : (
            Object.entries(grouped).map(([section, items]) => (
              <div key={section} className="mb-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-3 py-1.5">{section}</p>
                {items.map((item) => {
                  const idx = flat.findIndex((f) => f.id === item.id);
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => { navigate(item.path); setOpen(false); }}
                      onMouseEnter={() => setActiveIdx(idx)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition ${
                        idx === activeIdx ? 'bg-primary/10 text-primary' : 'hover:bg-secondary'
                      }`}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-sm flex-1">{item.label}</span>
                      {idx === activeIdx && <ArrowRight className="w-3.5 h-3.5" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-border px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-secondary font-mono">↑↓</kbd> navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded bg-secondary font-mono">↵</kbd> select</span>
          </div>
          <div className="flex items-center gap-1">
            <CommandIcon className="w-3 h-3" /> K to toggle
          </div>
        </div>
      </div>
    </div>
  );
};
